/**
 * Display formatting. The API quotes a 0–1 probability price (plus an American odds string);
 * odds screens lead with American, so that is the default, and the top-bar toggle switches the
 * whole board at once.
 */

export type OddsFormat = "american" | "decimal" | "cents" | "percent";

export const ODDS_FORMATS: { value: OddsFormat; label: string }[] = [
  { value: "american", label: "American" },
  { value: "decimal", label: "Decimal" },
  { value: "cents", label: "Cents ¢" },
  { value: "percent", label: "Chance %" },
];

const DASH = "—";

/** 0–1 probability price → American odds. Even money reads "+100". */
export function americanFromPrice(price: number): string {
  if (!(price > 0 && price < 1)) return DASH;
  if (Math.abs(price - 0.5) < 1e-9) return "+100";
  const odds = price > 0.5 ? -(price / (1 - price)) * 100 : ((1 - price) / price) * 100;
  const r = Math.round(odds);
  return r > 0 ? `+${r}` : String(r);
}

/** 0.673 → "67.3¢"; whole cents drop the ".0". */
export function fmtCents(price: number): string {
  const c = Math.round(price * 1000) / 10;
  return `${Number.isInteger(c) ? c : c.toFixed(1)}¢`;
}

/**
 * A price in the chosen format. American prefers the API's own string (what the venue quotes);
 * after a stream delta there is none, so it is derived from the price.
 */
export function fmtOdds(price: number, american: string | null | undefined, format: OddsFormat): string {
  switch (format) {
    case "decimal":
      return (1 / price).toFixed(2);
    case "cents":
      return fmtCents(price);
    case "percent":
      return `${Math.round(price * 1000) / 10}%`;
    case "american":
    default:
      return american && american.trim() ? american.trim() : americanFromPrice(price);
  }
}

/** Compact size: 984 → "$984", 9600 → "$9.6k", 23000 → "$23k", 1.2e6 → "$1.2M". */
export function fmtUsd(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return DASH;
  if (n < 999.5) return `$${Math.round(n)}`;
  const k = n / 1000;
  if (k < 9.95) return `$${k.toFixed(1)}k`;
  if (k < 999.5) return `$${Math.round(k)}k`;
  const m = n / 1_000_000;
  return m < 9.95 ? `$${m.toFixed(1)}M` : `$${Math.round(m)}M`;
}

/** A side's line: over/under "o8.5" / "u8.5"; anything else signed, "+1.5" / "-1.5" / "PK". */
export function fmtLine(sideKey: string, line: number): string {
  if (sideKey === "over") return `o${line}`;
  if (sideKey === "under") return `u${line}`;
  return line === 0 ? "PK" : line > 0 ? `+${line}` : String(line);
}

/** The line text for a side, or null when its market has no lines. */
export function sideLineText(side: { sideKey: string; line: number | null }): string | null {
  return side.line === null ? null : fmtLine(side.sideKey, side.line);
}

function startOfDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/**
 * When a game starts, the way odds screens show it: a bare time today, "Tomorrow", a weekday
 * within the week, a date beyond. `live` once the start time has passed.
 */
export function fmtStart(iso: string | undefined, now: Date = new Date()): {
  day: string | null;
  time: string;
  live: boolean;
} {
  const d = iso ? new Date(iso) : null;
  if (!d || Number.isNaN(d.getTime())) return { day: null, time: "TBD", live: false };
  const days = Math.round((startOfDay(d) - startOfDay(now)) / 86_400_000);
  const day =
    days === 0
      ? null
      : days === 1
        ? "Tomorrow"
        : days > 1 && days < 7
          ? d.toLocaleDateString(undefined, { weekday: "short" })
          : d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  const time = d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  return { day, time, live: d.getTime() <= now.getTime() };
}

/** "just now" / "12s ago" / "3m ago" / "2h ago". */
export function fmtAgo(ms: number): string {
  if (ms < 5_000) return "just now";
  if (ms < 60_000) return `${Math.round(ms / 1000)}s ago`;
  if (ms < 3_600_000) return `${Math.round(ms / 60_000)}m ago`;
  return `${Math.round(ms / 3_600_000)}h ago`;
}
